import { toast } from 'react-toastify'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { AudienceBuilder } from '../audience-builder'
import { Loading } from 'components/messages/loading'
import { DefaultAudienceCards, TermsAlert } from './'
import { UserLayout } from 'components/layout/UserLayout'
import { SavedAudience } from '@marketing-milk/interfaces'
import { AudienceRecalculation } from './audience-recalculation'
import { audienceBuilderService } from '@marketing-milk/frontend'
import { ServerError } from 'components/server-request/server-error/ServerError'
import { AudiencesTable } from 'components/common-tables/audiences-table/AudiencesTable'
import { CustomAudienceTermsAndConditions, SavedAudienceType } from '@marketing-milk/interfaces'
import { DeleteAudienceModal } from 'components/audience/delete-audience-modal/DeleteAudienceModal'
import { useGetBusinessID, useGetBusinessIfExists } from '@marketing-milk/frontend'
import { ViewAudienceModal } from '@marketing-milk/frontend'
import { ListPageBar } from 'components/layout/PageBar'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { useFetchAllAudiences } from 'hooks/audiences/useFetchAllAudiences'
import { NoItemsCreated } from 'components/NoItemsCreated/NoItemsCreated'
import { CommentThread } from 'components/comments/CommentThread'
import { CommentIndicator } from 'components/comments/CommentIndicator'
import { countUnresolvedRoots } from 'components/comments/commentCounts'
import { useComments } from 'hooks/api/useComments'
import { readCommentDeepLink } from 'components/comments/commentDeepLink'
import { light } from 'pages/business/sprint-packets/tokens'
import { audiencesUrl } from '../audienceUrl'

export function ListAudiences() {
  const businessID = useGetBusinessID()
  const business = useGetBusinessIfExists()
  const [error, setError] = useState<Error>()
  const [loading, setLoading] = useState(true)
  const [terms, setTerms] = useState<CustomAudienceTermsAndConditions>()
  const { audiences, refetch } = useFetchAllAudiences(businessID)
  const [search, setSearch] = useState('')
  const [deleteModalState, verifyDelete] = useState<SavedAudience | undefined>()
  const [viewModalState, viewAudience] = useState<SavedAudience | undefined>()
  const [editAudienceState, editAudience] = useState<SavedAudience | undefined>()
  const [recalcModalState, viewRecalc] = useState<SavedAudience | undefined>()
  // Audience comment thread, anchored to whichever audience the preview shows.
  const [commentOpen, setCommentOpen] = useState(false)
  const handledDeepLinkRef = useRef(false)

  // Unresolved-comment badge for the open audience's preview trigger. Shares the
  // mounted thread's query key (so react-query dedupes the fetch and refreshes the
  // count on resolve); disabled until a preview is open via the null anchor.
  const audienceAnchorID = viewModalState ? String(viewModalState.id) : null
  const { comments: audienceComments } = useComments(
    Number(businessID),
    'audience',
    audienceAnchorID
  )
  const audienceUnresolvedCount = audienceAnchorID
    ? countUnresolvedRoots(audienceComments, 'audience', audienceAnchorID)
    : 0

  // Close the preview and its comment thread together so the persistent thread
  // can't linger against a null anchor after the modal closes.
  const closeAudiencePreview = () => {
    setCommentOpen(false)
    viewAudience(undefined)
  }

  // Slack audience comment deep-link (`?anchorType=audience&anchorId=`): once the
  // audiences load, open the matching audience's preview + thread. A deleted or
  // foreign id simply won't match the business-scoped list, so it's a no-op.
  useEffect(() => {
    if (!audiences || handledDeepLinkRef.current) return
    const link = readCommentDeepLink()
    if (!link || link.anchorType !== 'audience') return
    handledDeepLinkRef.current = true
    const match = audiences.find(a => String(a.id) === link.anchorId)
    if (!match) return
    viewAudience(match)
    setCommentOpen(true)
  }, [audiences])

  useEffect(() => {
    setLoading(true)
    async function checkTerms() {
      try {
        const busTerms = await audienceBuilderService.checkTermsAndConditions(businessID)
        setTerms(busTerms)
      } catch (e: any) {
        setError(e)
      } finally {
        setLoading(false)
      }
    }

    checkTerms()
  }, [businessID])

  // The whole list is already in memory, so search is a filter rather than a query.
  const visibleAudiences = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return audiences ?? []
    return (audiences ?? []).filter(a => a.name.toLowerCase().includes(term))
  }, [audiences, search])

  async function onSetDefault(audience: SavedAudience, isDefault: boolean) {
    try {
      await audienceBuilderService.updateAudience(audience.businessID, audience.id, {
        isDefault: isDefault,
      })
      refetch()
      toast.success(`${audience.name} default status has been updated.`)
    } catch {
      toast.error(`Sorry, we had trouble updating ${audience.name}'s default status.`)
    }
  }

  return (
    <>
      <UserLayout>
        {error && <ServerError error={error} />}
        <TermsAlert termsData={terms} />
        <ListPageBar
          subject="Audiences"
          search={{ value: search, onChange: setSearch, placeholder: 'Search audiences' }}
          actions={<AudienceBuilder mode="create" onAudienceCreation={refetch} />}
          status={
            audiences ? `${visibleAudiences.length} of ${audiences.length} audiences` : undefined
          }
        />

        <FullPageContent className="flex w-full flex-col space-x-4 p-2">
          {audiences ? (
            <>
              {audiences.length > 0 ? (
                <div className="h-full">
                  <DefaultAudienceCards
                    onEdit={editAudience}
                    onView={viewAudience}
                    onDelete={verifyDelete}
                    onRecalc={viewRecalc}
                    onSetDefault={onSetDefault}
                    defaultAudiences={audiences.filter(audience => audience.isDefault)}
                  />

                  <h3 className="px-2 pb-1 pt-4 text-xs font-semibold uppercase tracking-wide text-mm-neutral-1-600">
                    All Audiences
                  </h3>
                  <AudiencesTable
                    onSetDefault={onSetDefault}
                    markUnavailable
                    audiences={visibleAudiences}
                    emptyMessage={`No audiences match “${search}”`}
                    deleteAudience={verifyDelete}
                    editAudience={editAudience}
                    recalcAudience={viewRecalc}
                    viewAudience={viewAudience}
                  />
                </div>
              ) : (
                <NoItemsCreated
                  title="Hmmm.. It looks like you haven't created an Audience yet, no worries!"
                  subtitle="Click the button below and create your first audience"
                >
                  <AudienceBuilder mode="create" onAudienceCreation={refetch} />
                </NoItemsCreated>
              )}
            </>
          ) : (
            <Loading />
          )}
        </FullPageContent>
      </UserLayout>

      {editAudienceState && (
        <AudienceBuilder
          mode="edit"
          audience={editAudienceState}
          onAudienceCreation={() => editAudience(undefined)}
          openModal={!!editAudienceState}
          selectedWizard={SavedAudienceType.HumanMade}
          hideToggle={true}
          onCloseModal={() => editAudience(undefined)}
        />
      )}

      {viewModalState && (
        <>
          <ViewAudienceModal
            onHideModal={closeAudiencePreview}
            onRecalc={viewRecalc}
            showModal={!!viewModalState}
            audiences={[viewModalState]}
            headerActions={
              <CommentIndicator
                palette={light}
                label={viewModalState.name}
                onClick={() => setCommentOpen(true)}
                unresolvedCount={audienceUnresolvedCount}
              />
            }
          />
          <CommentThread
            open={commentOpen}
            onClose={() => setCommentOpen(false)}
            businessID={Number(businessID)}
            anchor={{
              type: 'audience',
              id: String(viewModalState.id),
              label: viewModalState.name,
            }}
            notificationContext={{
              header: `Audience — ${viewModalState.name}`,
              pageUrlBase: audiencesUrl(Number(businessID)),
              slackChannelId: business?.slackChannelId,
            }}
          />
        </>
      )}
      <AudienceRecalculation
        onHideModal={() => viewRecalc(undefined)}
        onCalculateAudience={refetch}
        showModal={!!recalcModalState}
        audience={recalcModalState!}
      />
      <DeleteAudienceModal
        audience={deleteModalState}
        onDelete={() => {
          verifyDelete(undefined)
          refetch()
        }}
      />
    </>
  )
}
